import { useEffect, useRef, useState } from 'react';

/**
 * Lightweight in-view hook backed by IntersectionObserver.
 * Returns [ref, inView]. Once an element has entered the viewport, the flag
 * stays true (one-shot reveal). Honors a small fallback timeout in case the
 * observer never fires (e.g., element already on-screen at mount).
 */
const useInView = ({ threshold = 0.15, rootMargin = '0px 0px -10% 0px', fallbackMs = 600 } = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(node);

    const t = setTimeout(() => {
      // If the element is already in the viewport at mount but the observer
      // hasn't fired yet, trigger reveal via getBoundingClientRect check.
      const rect = node.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        setInView(true);
        observer.disconnect();
      }
    }, fallbackMs);

    return () => {
      observer.disconnect();
      clearTimeout(t);
    };
  }, [threshold, rootMargin, fallbackMs, inView]);

  return [ref, inView];
};

export default useInView;
